/* @flow */
// typecheck: ignore file
import React from 'react';
import AlertDialog from 'components/AlertDialog.jsx';
import Button from 'components/Button.jsx';
import Checkbox from 'components/Checkbox.jsx';
import Coin from 'components/Coin.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import Device from 'entities/Device';
import DeviceActions from 'actions/DeviceActions';
import Devices from 'collections/Devices';
import Divider from 'components/Divider.jsx';
import Glyph from 'components/Glyph.jsx';
import Legend from 'components/Legend.jsx';
import Link from 'components/Link.jsx';
import LinkSmsDeviceModal from 'user/components/LinkSmsDeviceModal.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import ResentConfirmationEmail from 'settings/components/ResentConfirmationEmail.jsx';
import Text from 'components/Text.jsx';
import TextAppLinkForm from 'user/components/TextAppLinkForm.jsx';
import ToastActions from 'actions/ToastActions';
import WebNotificationStore from 'notifications/WebNotificationStore';
import { mountModal } from 'lib/modal';

import NewEmailDeviceForm from './NewEmailDeviceForm.jsx';
import NotificationSettingsViewDesktop from './NotificationSettingsViewDesktop.jsx';
import NotificationSettingsViewPreferences from './NotificationSettingsViewPreferences.jsx';

import './styles/NotificationSettingsView.scss';

type Props = {
  devices: Devices;
  showSmsDevices: boolean,
  showNotificationPreferences: boolean,
};

class NotificationSettingsView extends React.Component {
  props: Props;

  onDeviceStatusChange = (device: Device) => {
    // if we are disabling a device and there's only 1 left.
    if (device.isEnabled() && this.props.devices.enabled().length <= 1) {
      mountModal(
        <AlertDialog
          title="Stay in touch"
          subtext="To receive messages from Remind, turn on at least one type of notification."
        />,
        this
      );

      return;
    }

    const savedState = device.isEnabled();

    const onConfirm = () => {
      device.setEnabled(!device.isEnabled());

      const promise = device.isEnabled() ?
        DeviceActions.enable(device) :
        DeviceActions.disable(device);

      const onSucces = () => {
        ToastActions.showSuccessToast(
          device.isEnabled() ?
            `Notifications turned on for ${device.getLabel()}` :
            `Notifications turned off for ${device.getLabel()}`
        );
      };

      const onError = () => {
        device.setEnabled(savedState);
        ToastActions.showDefaultErrorToast();
      };

      promise.then(onSucces, onError);
      this.forceUpdate();
    };

    if (savedState === false) {
      onConfirm();
      return;
    }

    mountModal(
      <ConfirmDialog
        eventScreenName="NotificationSettingsView"
        title={
          <div>
            Turn off {device.getNotificationType()} for
            <br />{device.getLabel()}?
          </div>
        }
        actionButtonText="Turn off"
        cancelButtonText="Cancel"
        onConfirm={onConfirm}
        subtext={
          `You will no longer get ${device.getMessageType()} when you receive messages on Remind.`
        }
      />,
      this
    );
  }

  renderUnconfirmedEmailSubtext = (device: Device) => {
    let text = null;
    if (device.cannotBeEnabled()) {
      text = (
        <div>
          Please
          {' '}
          <ResentConfirmationEmail email={device}>
            confirm this email
          </ResentConfirmationEmail>
          {' '}
          to receive notifications.
        </div>
      );
    } else {
      text = (
        <div>
          Pending confirmation.
          {' '}
          <ResentConfirmationEmail email={device}>
            Resend email
          </ResentConfirmationEmail>
        </div>
      );
    }

    return (
      <span>
        <Coin border="flame" size="xxsmall">
          <Glyph name="explanation" color="flame" solo />
        </Coin>
        {text}
      </span>
    );
  }

  renderDevice = (device: Device) => {
    let action = null;
    if (device.getType() === 'sms') {
      action = (
        <span>
          {' '}
          •
          {' '}
          <ModalTrigger modal={<LinkSmsDeviceModal replaceDevice={device} />}>
            <Link>Edit</Link>
          </ModalTrigger>
        </span>
      );
    }

    return (
      <div
        key={device.id}
        className="NotificationSettingsView-device">
        <Checkbox
          onChange={() => this.onDeviceStatusChange(device)}
          disabled={device.cannotBeEnabled()}
          checked={device.isEnabled()}>
          <div>
            <Glyph
              name={device.getGlyphName()}
              size="medium"
              className="NotificationSettingsView-device-glyph"
            />
            <Text
              color={device.isVerified() ? 'pitch' : 'thunder'}
              weight="semibold">
              {device.getLabel()}
              {' '}
              {
                device.getType() === 'email' && !device.isVerified() &&
                  this.renderUnconfirmedEmailSubtext(device)
              }
            </Text>
            {action}
          </div>
        </Checkbox>
      </div>
    );
  }

  renderEmailDevices = () => {
    const devices = this.props.devices.type('email');
    if (devices.length === 0) {
      return <NewEmailDeviceForm />;
    }
    return (
      <div>
        {devices.map(this.renderDevice)}
      </div>
    );
  }

  renderPushNotificationSettings = () => {
    const devices = new Devices(
      [].concat(
        this.props.devices.type('apns'), // apple
        this.props.devices.type('amd'),  // amazon
        this.props.devices.type('gcm')   // google
      )
    );

    if (devices.length <= 0) {
      return (
        <div>
          <Legend>
            Push
            <p>
              Send and receive messages instantly on your phone.
            </p>
          </Legend>
          <TextAppLinkForm />
        </div>
      );
    }

    return (
      <div>
        <Legend>Push</Legend>
        {devices.map(this.renderDevice)}
      </div>
    );
  }

  renderSmsDevices = () => {
    const devices = this.props.devices.type('sms');
    if (devices.length <= 0) {
      return (
        <ModalTrigger modal={<LinkSmsDeviceModal />}>
          <Button
            size="xsmall"
            className="NotificationSettingsView-addSmsDeviceBtn"
            level="secondary"
            eventElementName="NotificationSettingsView-addSmsDeviceBtn">
            Add a mobile device
          </Button>
        </ModalTrigger>
      );
    }

    return devices.map(this.renderDevice);
  }

  render() {
    return (
      <div className="NotificationSettingsView">
        {this.renderPushNotificationSettings()}

        {
          this.props.showSmsDevices &&
            <div>
              <Divider />
              <Legend>Text message</Legend>
              {this.renderSmsDevices()}
            </div>
        }

        <Divider />

        <Legend>Email</Legend>
        {this.renderEmailDevices()}

        {
          WebNotificationStore.areNotificationsCompatible() &&
            <div>
              <Divider />
              <NotificationSettingsViewDesktop />
            </div>
        }

        {
          this.props.showNotificationPreferences &&
            <div>
              <Divider />
              <NotificationSettingsViewPreferences />
            </div>
        }
      </div>
    );
  }
}

export default NotificationSettingsView;



// WEBPACK FOOTER //
// ./src/settings/components/NotificationSettingsView.jsx